import React, { useEffect, useState, useTransition } from "react";
import axios from "axios";
import { useAppSelector } from "app/hooks";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Modal from "shared/Modal/Modal";
import { notifications } from "utils/notifications";
import SmallLoader from "./SmallLoader";

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: "Pending" | "Reviewed" | "Replied";
  notes?: string;
  createdAt: string;
}

const ITEMS_PER_PAGE = 8;

const ContactManager: React.FC = () => {
  const { userInfo } = useAppSelector((state) => state.users);
  const token = userInfo?.token;

  const [inquiries, setInquiries] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedInquiry, setSelectedInquiry] = useState<ContactMessage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Status and notes form states
  const [editStatus, setEditStatus] = useState<"Pending" | "Reviewed" | "Replied">("Pending");
  const [editNotes, setEditNotes] = useState("");

  // Filtering and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isPending, startTransition] = useTransition();

  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL || "",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const fetchInquiries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/api/contacts");
      setInquiries(response.data);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchInquiries();
    }
  }, [token]);

  const handleOpenDetailModal = (inquiry: ContactMessage) => {
    setSelectedInquiry(inquiry);
    setEditStatus(inquiry.status);
    setEditNotes(inquiry.notes || "");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInquiry(null);
  };

  const handleSaveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInquiry) return;

    setIsSaving(true);
    try {
      const response = await axiosInstance.put(`/api/contacts/${selectedInquiry._id}`, {
        status: editStatus,
        notes: editNotes,
      });

      // Update in local state
      setInquiries((prev) =>
        prev.map((item) => (item._id === selectedInquiry._id ? response.data : item))
      );
      notifications.success("Inquiry updated successfully!");
      handleCloseModal();
    } catch (err: any) {
      console.error(err);
      notifications.error("Failed to update inquiry.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteInquiry = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this inquiry?")) {
      setIsDeleting(id);
      try {
        await axiosInstance.delete(`/api/contacts/${id}`);
        setInquiries((prev) => prev.filter((item) => item._id !== id));
        notifications.success("Inquiry deleted successfully!");
        if (selectedInquiry?._id === id) {
          handleCloseModal();
        }
      } catch (err: any) {
        console.error(err);
        notifications.error("Failed to delete inquiry.");
      } finally {
        setIsDeleting(null);
      }
    }
  };

  // Filter inquiries
  const filteredInquiries = inquiries.filter((inquiry) => {
    const term = searchTerm.toLowerCase();
    return (
      inquiry.name.toLowerCase().includes(term) ||
      inquiry.email.toLowerCase().includes(term) ||
      inquiry.subject.toLowerCase().includes(term) ||
      inquiry.message.toLowerCase().includes(term)
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredInquiries.length / ITEMS_PER_PAGE);
  const paginatedInquiries = filteredInquiries.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    startTransition(() => {
      setSearchTerm(e.target.value);
      setCurrentPage(1);
    });
  };

  const handlePageChange = (page: number) => {
    startTransition(() => {
      setCurrentPage(page);
    });
  };

  const getStatusBadge = (status: "Pending" | "Reviewed" | "Replied") => {
    switch (status) {
      case "Pending":
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/30">
            Pending
          </span>
        );
      case "Reviewed":
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-500/10 text-blue-500 border border-blue-500/30">
            Reviewed
          </span>
        );
      case "Replied":
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
            Replied
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 text-gray-100 min-h-screen">
      <div className="container mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-white">Contact Inquiries</h2>
            <p className="text-neutral-400 text-sm mt-1">
              Manage incoming customer messages and support tickets
            </p>
          </div>

          {/* Search bar */}
          <div className="relative w-full sm:w-80">
            <input
              type="text"
              placeholder="Search by name, email, text..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-4 py-2 bg-neutral-900 border border-neutral-800 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-white rounded-xl placeholder-neutral-500 outline-none transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white text-lg"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Loader State */}
        {(loading || isPending) && (
          <div className="flex justify-center items-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl mb-6">
            Error: {error}
          </div>
        )}

        {/* Inquiries Table */}
        {!loading && !error && (
          <div className="overflow-x-auto bg-neutral-900/40 border border-neutral-800/80 rounded-2xl shadow-xl">
            <table className="min-w-full table-auto border-collapse text-left text-sm text-neutral-300">
              <thead>
                <tr className="border-b border-neutral-800 text-white font-medium bg-neutral-900/60">
                  <th className="px-6 py-4">Received Date</th>
                  <th className="px-6 py-4">Sender Info</th>
                  <th className="px-6 py-4">Subject</th>
                  <th className="px-6 py-4">Message Snippet</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800 bg-transparent">
                {paginatedInquiries.length > 0 ? (
                  paginatedInquiries.map((inquiry) => (
                    <tr
                      key={inquiry._id}
                      className="hover:bg-neutral-800/20 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-neutral-400">
                        {new Date(inquiry.createdAt).toLocaleString("en-US", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-white">{inquiry.name}</div>
                        <div className="text-xs text-neutral-400 mt-0.5">{inquiry.email}</div>
                        {inquiry.phone && (
                          <div className="text-xs text-primary-400/80 mt-0.5">{inquiry.phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 font-medium text-neutral-200">
                        {inquiry.subject}
                      </td>
                      <td className="px-6 py-4 max-w-xs truncate text-neutral-400">
                        {inquiry.message}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(inquiry.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center items-center gap-3">
                          <button
                            className="text-primary-500 hover:text-primary-400 text-sm font-semibold transition-colors"
                            onClick={() => handleOpenDetailModal(inquiry)}
                          >
                            View & Triage
                          </button>
                          <button
                            className="text-red-500 hover:text-red-400 text-sm font-semibold transition-colors disabled:opacity-50"
                            disabled={isDeleting === inquiry._id}
                            onClick={() => handleDeleteInquiry(inquiry._id)}
                          >
                            {isDeleting === inquiry._id ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-neutral-500">
                      No inquiries found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
            <span className="text-xs text-neutral-400">
              Showing {Math.min(ITEMS_PER_PAGE * (currentPage - 1) + 1, filteredInquiries.length)} to{" "}
              {Math.min(ITEMS_PER_PAGE * currentPage, filteredInquiries.length)} of {filteredInquiries.length} inquiries
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded bg-neutral-900 border border-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-800 transition-colors text-sm"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                      currentPage === page
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-900 border border-neutral-800 hover:bg-neutral-800"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded bg-neutral-900 border border-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-800 transition-colors text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Inquiry Detail & Edit Modal */}
      {isModalOpen && selectedInquiry && (
        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          <form onSubmit={handleSaveDetails} className="p-6 text-gray-100">
            <h3 className="text-xl font-bold text-white mb-6 border-b border-neutral-800 pb-4">
              Inquiry Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <span className="block text-xs font-semibold text-neutral-500 uppercase">
                  Sender Name
                </span>
                <span className="text-white font-medium text-base">
                  {selectedInquiry.name}
                </span>
              </div>

              <div>
                <span className="block text-xs font-semibold text-neutral-500 uppercase">
                  Received Time
                </span>
                <span className="text-neutral-300">
                  {new Date(selectedInquiry.createdAt).toLocaleString()}
                </span>
              </div>

              <div>
                <span className="block text-xs font-semibold text-neutral-500 uppercase">
                  Email Address
                </span>
                <a
                  href={`mailto:${selectedInquiry.email}`}
                  className="text-primary-400 hover:underline break-all"
                >
                  {selectedInquiry.email}
                </a>
              </div>

              {selectedInquiry.phone && (
                <div>
                  <span className="block text-xs font-semibold text-neutral-500 uppercase">
                    Phone Number
                  </span>
                  <a
                    href={`tel:${selectedInquiry.phone}`}
                    className="text-primary-400 hover:underline"
                  >
                    {selectedInquiry.phone}
                  </a>
                </div>
              )}
            </div>

            <div className="mb-6">
              <span className="block text-xs font-semibold text-neutral-500 uppercase mb-1">
                Subject
              </span>
              <span className="text-white font-semibold text-base">
                {selectedInquiry.subject}
              </span>
            </div>

            <div className="mb-6 p-4 rounded-xl bg-neutral-950/60 border border-neutral-850">
              <span className="block text-xs font-semibold text-neutral-500 uppercase mb-2">
                Message Body
              </span>
              <p className="text-neutral-300 leading-relaxed whitespace-pre-wrap">
                {selectedInquiry.message}
              </p>
            </div>

            {/* Triaging Fields */}
            <div className="space-y-4 border-t border-neutral-800 pt-6">
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Update Status
                </label>
                <select
                  value={editStatus}
                  onChange={(e) =>
                    setEditStatus(e.target.value as "Pending" | "Reviewed" | "Replied")
                  }
                  className="w-full px-4 py-2 bg-neutral-900 border border-neutral-800 rounded-xl text-white outline-none focus:border-primary-500"
                >
                  <option value="Pending">Pending</option>
                  <option value="Reviewed">Reviewed</option>
                  <option value="Replied">Replied</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Admin Internal Notes
                </label>
                <textarea
                  rows={4}
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add internal follow-up notes here..."
                  className="w-full px-4 py-2 bg-neutral-900 border border-neutral-800 rounded-xl text-white outline-none focus:border-primary-500 resize-none"
                ></textarea>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-8 border-t border-neutral-800 pt-4">
              <button
                type="button"
                className="px-5 py-2.5 rounded-xl border border-neutral-800 hover:bg-neutral-800 transition-colors text-sm"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <ButtonPrimary
                type="submit"
                disabled={isSaving}
                className="px-5 py-2.5 flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <SmallLoader />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </ButtonPrimary>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ContactManager;
